import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./NewArrivalsStories.module.css";
import WebService from "../../Services/WebService";
import { mediaUrl } from "../../Services/mediaUrl";
import { orderedProductImages } from "../../Services/productImages";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaBolt } from "react-icons/fa";

interface NewArrivalItem {
  product_id: number;
  category_id: number;
  product_type: string;
  product_name: string;
  images?: { file_name: string }[];
  discount?: number;
}

type Pagination = {
  current_page: number;
  page_size: number;
  total_records: number;
  total_pages: number;
};

type ApiResp = {
  status_code?: number;
  message?: string;
  data?: {
    pagination?: Pagination;
    products?: any[];
  };
};

const PAGE_SIZE = 10;
const CARD_GAP_FALLBACK = 190;

const toNum = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

const keyOf = (item: { product_type: string; category_id: number; product_id: number }) =>
  `${item.product_type}:${item.category_id}:${item.product_id}`;

const NewArrivalsStories: React.FC = () => {
  const [items, setItems] = useState<NewArrivalItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const storiesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // NEW: track which composite keys we've already shown (prevents duplicates)
  const seenKeysRef = useRef<Set<string>>(new Set());

  // NEW: track which pages are currently being fetched (prevents double fetch of the same page)
  const inFlightPagesRef = useRef<Set<number>>(new Set());

  // map API → UI model (matches your payload)
  const mapProducts = (res: ApiResp): NewArrivalItem[] => {
    const raw = res?.data?.products ?? [];
    if (!Array.isArray(raw)) return [];

    return raw
      .map((p: any): NewArrivalItem | null => {
        const product_id = toNum(p.product_id);
        const category_id = toNum(p.category_id);
        if (!Number.isFinite(product_id) || !Number.isFinite(category_id)) return null;

        return {
          product_id,
          category_id,
          product_type: (p.product_type || p.type_category || "PRODUCT").toString().toUpperCase(),
          product_name: p.product_name || p.name || "",
          images: orderedProductImages(p),
          // discount is optional
          discount:
            toNum(
              p.customer_sale_discount_per ??
                p.distributor_sale_discount_per ??
                p.discount ??
                0
            ) || 0,
        };
      })
      .filter(Boolean) as NewArrivalItem[];
  };

  const updateHasMore = (res: ApiResp, rawBatchLen: number) => {
    const pg = res?.data?.pagination;
    if (pg && typeof pg.current_page === "number" && typeof pg.total_pages === "number") {
      setHasMore(pg.current_page < pg.total_pages);
      return;
    }
    // Fallback if pagination missing: use RAW length returned by server (not deduped length)
    setHasMore(rawBatchLen >= PAGE_SIZE);
  };

  const refreshArrowState = () => {
    const el = storiesRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const left = el.scrollLeft;
    const maxLeft = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(left > 0);
    setCanScrollRight(left < maxLeft - 2);
  };

  const fetchPage = useCallback(
    async (nextPage: number, append = true) => {
      // prevent duplicate calls for the same page
      if (inFlightPagesRef.current.has(nextPage)) return;

      // don't fetch past end
      if (nextPage > 1 && !hasMore) return;

      inFlightPagesRef.current.add(nextPage);

      if (nextPage === 1) {
        setInitialLoading(true);
        // reset seen keys for a true refresh
        seenKeysRef.current.clear();
      } else {
        setFetchingMore(true);
      }

      try {
        const res = await WebService.getAPI<ApiResp>({
          action: `/v1/door-management/dashboard/new-arrivals?page_number=${nextPage}&limit=${PAGE_SIZE}`,
        });

        // 1) map server payload
        const rawBatch = mapProducts(res);
        const rawBatchLen = rawBatch.length;

        // 2) de-duplicate by composite key
        const deduped = rawBatch.filter((it) => {
          const k = keyOf(it);
          if (seenKeysRef.current.has(k)) return false;
          seenKeysRef.current.add(k);
          return true;
        });

        // 3) set items
        setItems((prev) => {
          if (!append || nextPage === 1) return deduped;
          return [...prev, ...deduped];
        });

        setPage(nextPage);
        updateHasMore(res, rawBatchLen);
      } catch {
        if (nextPage === 1) {
          setItems([]);
          setHasMore(false);
        } else {
          setHasMore(false);
        }
      } finally {
        if (nextPage === 1) setInitialLoading(false);
        setFetchingMore(false);
        inFlightPagesRef.current.delete(nextPage);
        // update arrows after DOM updates
        setTimeout(refreshArrowState, 0);
      }
    },
    [hasMore]
  );

  useEffect(() => {
    fetchPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onResize = () => refreshArrowState();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // light throttle: avoid firing fetch multiple times within a single scroll frame
  const scrollTickingRef = useRef(false);
  const handleScroll = () => {
    refreshArrowState();
    const el = storiesRef.current;
    if (!el || initialLoading || fetchingMore || !hasMore) return;

    if (!scrollTickingRef.current) {
      scrollTickingRef.current = true;
      window.requestAnimationFrame(() => {
        const nearEnd =
          el.scrollLeft + el.clientWidth >=
          el.scrollWidth - Math.max(300, el.clientWidth / 2);
        if (nearEnd) fetchPage(page + 1, true);
        scrollTickingRef.current = false;
      });
    }
  };

  const handleStoryClick = (item: NewArrivalItem) => {
    if (!Number.isFinite(item.product_id) || !Number.isFinite(item.category_id)) return;
    navigate(`/watchlist/${item.product_type}/${item.category_id}/${item.product_id}`);
  };

  const scrollByCards = (dir: "left" | "right") => {
    const el = storiesRef.current;
    if (!el) return;

    const card = el.querySelector(`.${styles.story}`) as HTMLElement | null;
    const gap = card ? card.offsetWidth + 38 : CARD_GAP_FALLBACK;
    const delta = (dir === "left" ? -1 : 1) * gap * 2;

    el.scrollBy({ left: delta, behavior: "smooth" });

    if (dir === "right") {
      window.requestAnimationFrame(() => {
        setTimeout(() => {
          refreshArrowState();
          const nearEnd =
            el.scrollLeft + el.clientWidth >=
            el.scrollWidth - Math.max(300, el.clientWidth / 2);
          if (nearEnd && hasMore && !fetchingMore && !initialLoading) {
            fetchPage(page + 1, true);
          }
        }, 180);
      });
    }
  };

  if (initialLoading && items.length === 0) {
    return <div className={styles.storiesLoading}>Loading new arrivals...</div>;
  }
  if (!items.length) return null;

  return (
    <div className={styles.storiesOuter}>
      <div className={styles.headerRow}>
        <div className={styles.onSaleLabel}>
          <span className={styles.bolt}><FaBolt /></span>
          <span className={styles.storiesTitle}>New Arrivals</span>
          <span className={styles.bolt}><FaBolt /></span>
        </div>
        <div className={styles.storiesArrows}>
          <button
            className={styles.arrowBtn}
            onClick={() => scrollByCards("left")}
            aria-label="Scroll left"
            disabled={!canScrollLeft}
          >
            <FaChevronLeft />
          </button>
          <button
            className={styles.arrowBtn}
            onClick={() => scrollByCards("right")}
            aria-label="Scroll right"
            disabled={!canScrollRight && !hasMore && !fetchingMore}
          >
            <FaChevronRight />
          </button>
        </div>
      </div>

      <div className={styles.storiesBar} ref={storiesRef} onScroll={handleScroll}>
        {items.map((item) => {
          const imgUrl =
            Array.isArray(item.images) && item.images.length > 0 && item.images[0].file_name
              ? mediaUrl(item.images[0].file_name)
              : "/Assets/no-pictures.png";
          const doorName = item.product_name || "No name";
          return (
            <div
              key={keyOf(item)}
              className={styles.story}
              onClick={() => handleStoryClick(item)}
              tabIndex={0}
              title={doorName}
            >
              <div className={styles.storyCircle}>
                <img
                  src={imgUrl}
                  alt={doorName}
                  className={styles.storyImg}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/Assets/no-pictures.png";
                  }}
                  draggable={false}
                  loading="lazy"
                />
                {!!item.discount && item.discount > 0 && (
                  <div className={styles.discountTag}>-{item.discount}%</div>
                )}
              </div>
              <div className={styles.storyName}>
                {doorName.length > 13 ? doorName.slice(0, 12) + "…" : doorName}
              </div>
            </div>
          );
        })}

        {fetchingMore && hasMore && (
          <div className={styles.storiesLoading} style={{ minWidth: 120 }}>
            Loading…
          </div>
        )}
      </div>
    </div>
  );
};

export default NewArrivalsStories;
